import * as React from "react"
import { Bar, ComposedChart, CartesianGrid, XAxis, YAxis, Line } from "recharts"
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "@/components/ui/chart"
import { formatUSD, formatPercent } from "@/lib/format-utils"
import dayjs from "dayjs"
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area"

const SIMPLIFIED_RANGES = ["D0", "D1", "D2", "D3", "D4", "D5", "D6", "D7", "D8-14", "D15-30", "D31-60", "D61-90", "D91+"]

const PREMIUM_PALETTE = [
  "#1d4ed8",
  "#2563eb",
  "#3b82f6",
  "#60a5fa",
  "#7c3aed",
  "#8b5cf6",
  "#a78bfa",
  "#0d9488",
  "#14b8a6",
  "#2dd4bf",
  "#0891b2",
  "#06b6d4",
  "#22d3ee",
]

const getRangeColor = (range) => {
  const simplifiedMap = {
    'D0': "#1d4ed8",
    'D1': "#2563eb",
    'D2': "#3b82f6",
    'D3': "#60a5fa",
    'D4': "#7c3aed",
    'D5': "#8b5cf6",
    'D6': "#a78bfa",
    'D7': "#0d9488",
    'D8-14': "#14b8a6",
    'D15-30': "#2dd4bf",
    'D31-60': "#0891b2",
    'D61-90': "#06b6d4",
    'D91+': "#22d3ee"
  }

  if (simplifiedMap[range]) return simplifiedMap[range]

  if (range.startsWith('D')) {
    const day = parseInt(range.substring(1))
    if (!isNaN(day)) {
      return PREMIUM_PALETTE[day % PREMIUM_PALETTE.length]
    }
  }
  
  return '#3b82f6'
}

const getSourceDateText = (revenueDate, range, isSimplified) => {
  const rDate = dayjs(revenueDate)
  
  if (isSimplified && (range.includes('-') || range.endsWith('+'))) {
    if (range === 'D91+') return `Installed before ${rDate.subtract(91, 'day').format('MM-DD')}`
    const [start, end] = range.substring(1).split('-').map(Number)
    return `${rDate.subtract(end, "day").format("MM-DD")} ~ ${rDate
      .subtract(start, "day")
      .format("MM-DD")}`
  }
  
  const day = parseInt(range.substring(1))
  if (isNaN(day)) return range
  return rDate.subtract(day, 'day').format('MM-DD')
}

const RevenueMixChart = React.memo(({ data, displayMode, isSimplified, lastYearData }) => {
  // 构建去年数据的 Map（按日期查找）
  const lastYearMap = React.useMemo(() => {
    if (!lastYearData) return new Map()
    const map = new Map()
    lastYearData.forEach((item) => {
      // 将去年日期映射到今年日期
      const thisYearDate = dayjs(item.revenue_date).add(1, 'year').format('YYYY-MM-DD')
      map.set(thisYearDate, item.total || 0)
    })
    return map
  }, [lastYearData])

  const hasLastYearData = lastYearMap.size > 0

  const { chartData, allCohortKeys, chartConfig } = React.useMemo(() => {
    // 按日期升序排列（从左到右：早 → 晚）
    const sorted = [...data].sort((a, b) =>
      a.revenue_date.localeCompare(b.revenue_date),
    )

    const transformedData = sorted.map((item) => {
      const row = { 
        date: item.revenue_date,
        displayDate: item.display_date,
        total: item.total || 0,
        rawRanges: item.ranges || {},
        // 添加去年同期总收入
        lastYearTotal: lastYearMap.get(item.revenue_date) ?? null,
      }
      if (displayMode === "percentage") {
        Object.entries(item.ranges).forEach(([range, value]) => {
          row[range] = item.total > 0 ? (value / item.total) * 100 : 0
        })
      } else {
        Object.assign(row, item.ranges)
      }
      return row
    })

    const keys = new Set()
    transformedData.forEach(item => {
      Object.keys(item).forEach(k => {
        if (k.startsWith('D') && k !== 'date' && item[k] > 0) keys.add(k)
      })
    })

    let sortedKeys = []
    if (isSimplified) {
      sortedKeys = SIMPLIFIED_RANGES.filter(r => keys.has(r))
    } else {
      sortedKeys = Array.from(keys).sort((a, b) => {
        const aVal = parseInt(a.substring(1))
        const bVal = parseInt(b.substring(1))
        return aVal - bVal
      })
    }

    const config = {}
    sortedKeys.forEach(key => {
      config[key] = { label: key, color: getRangeColor(key) }
    })

    // 如果有去年数据，添加到 config 中
    if (hasLastYearData) {
      config['lastYearTotal'] = { label: 'Last Year Total', color: '#94a3b8' }
    }

    return { chartData: transformedData, allCohortKeys: sortedKeys, chartConfig: config }
  }, [data, displayMode, isSimplified, lastYearMap, hasLastYearData])

  const formatYAxis = React.useCallback((val) => {
    if (displayMode === "percentage") return `${val.toFixed(0)}%`
    return formatUSD(val)
  }, [displayMode])

  const minBarWidth = 40
  const chartWidth = Math.max(chartData.length * minBarWidth, 800)

  // 构建 Legend payload，包含去年数据
  const legendPayload = React.useMemo(() => {
    const payload = allCohortKeys.map(key => ({
      dataKey: key,
      value: key,
      color: chartConfig[key].color
    }))
    if (hasLastYearData) {
      payload.push({
        dataKey: 'lastYearTotal',
        value: 'Last Year Total',
        color: '#94a3b8'
      })
    }
    return payload
  }, [allCohortKeys, chartConfig, hasLastYearData])

  return (
    <ScrollArea className="w-full">
      <div style={{ width: chartWidth }}>
        <ChartContainer config={chartConfig} className="h-[400px] w-full">
          <ComposedChart
            data={chartData}
            width={chartWidth}
            height={400}
            margin={{ left: 12, right: 12, top: 20 }}
          >
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="displayDate"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={30}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={formatYAxis}
            />
            <ChartTooltip
              cursor={false}
              content={(props) => {
                const { active, payload } = props;
                if (active && payload && payload.length) {
                  // 分离柱状图数据和线数据
                  const barPayload = payload.filter(p => p.dataKey !== 'lastYearTotal')
                  const linePayload = payload.filter(p => p.dataKey === 'lastYearTotal')
                  
                  const sortedBarPayload = [...barPayload].sort((a, b) => {
                    const aIdx = SIMPLIFIED_RANGES.indexOf(a.dataKey);
                    const bIdx = SIMPLIFIED_RANGES.indexOf(b.dataKey);
                    return aIdx - bIdx;
                  });

                  // 合并：先显示去年数据，再显示今年各 cohort
                  const finalPayload = [...linePayload, ...sortedBarPayload]

                  return (
                    <ChartTooltipContent
                      {...props}
                      payload={finalPayload}
                      labelFormatter={(value) => `Revenue Date: ${value}`}
                      formatter={(value, name, item) => {
                        // 去年总收入特殊处理
                        if (name === 'lastYearTotal') {
                          const lastYearDate = dayjs(item.payload.date).subtract(1, 'year').format('YYYY-MM-DD')
                          const predictedTotal = item.payload.total || 0
                          return (
                            <div className="flex flex-col gap-1 w-full text-[10px]">
                              <div className="flex items-center justify-between gap-4 w-full">
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-2 h-2 rounded-full flex-shrink-0" 
                                    style={{ backgroundColor: '#94a3b8' }}
                                  />
                                  <span className="whitespace-nowrap text-muted-foreground">
                                    Last Year Total ({lastYearDate})
                                  </span>
                                </div>
                                <span className="font-bold tabular-nums">{value !== null ? formatUSD(value) : '-'}</span>
                              </div>
                              <div className="flex items-center justify-between gap-4 w-full">
                                <span className="whitespace-nowrap text-muted-foreground">
                                  Total (Predicted)
                                </span>
                                <span className="font-bold tabular-nums">{formatUSD(predictedTotal)}</span>
                              </div>
                            </div>
                          )
                        }

                        const sourceText = getSourceDateText(item.payload.date, name, isSimplified)
                        const rawValue = item.payload.rawRanges?.[name] || 0
                        const percentValue = item.payload.total > 0 ? rawValue / item.payload.total : 0
                        const valText = formatUSD(rawValue)
                        const pctText = formatPercent(percentValue)
                        const color = chartConfig[name]?.color
                        return (
                          <div className="flex items-center justify-between gap-4 w-full text-[10px]">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-2 h-2 rounded-full flex-shrink-0" 
                                style={{ backgroundColor: color }}
                              />
                              <span className="whitespace-nowrap text-muted-foreground">
                                {name} ({sourceText})
                              </span>
                            </div>
                            <span className="font-bold tabular-nums">
                              {valText} <span className="text-muted-foreground">({pctText})</span>
                            </span>
                          </div>
                        )
                      }}
                    />
                  );
                }
                return null;
              }}
            />
            <ChartLegend 
              content={
                <ChartLegendContent 
                  className="flex-wrap" 
                  payload={legendPayload}
                />
              } 
            />
            {[...allCohortKeys].reverse().map((key) => (
              <Bar
                key={key}
                dataKey={key}
                fill={chartConfig[key].color}
                stackId="a"
                radius={[0, 0, 0, 0]}
                isAnimationActive={false}
              />
            ))}
            {/* 去年同期总收入 - 虚线 */}
            {hasLastYearData && (
              <Line
                type="monotone"
                dataKey="lastYearTotal"
                stroke="#94a3b8"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
              />
            )}
          </ComposedChart>
        </ChartContainer>
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  )
})

RevenueMixChart.displayName = 'RevenueMixChart'

export default RevenueMixChart
